import React, { useEffect } from 'react';
import LessonLayout from '../components/LessonLayout';
import { Card, Row, Col, Alert, Badge } from 'react-bootstrap';

// METHOD 1: Basic Props (accessing via props object)
const UserCard1 = (props) => {
  console.log('👤 Method 1 - Basic Props:', props);
  return (
    <Card className="mb-3">
      <Card.Body>
        <Badge bg="primary" className="mb-2">Method 1: Basic Props</Badge>
        <p><strong>Name:</strong> {props.name}</p>
        <p className="mb-0"><strong>Age:</strong> {props.age}</p>
      </Card.Body>
    </Card>
  );
};

// METHOD 2: Destructured Props (most common pattern)
const UserCard2 = ({ name, age, city }) => {
  console.log('👤 Method 2 - Destructured Props:', { name, age, city });
  return (
    <Card className="mb-3">
      <Card.Body>
        <Badge bg="success" className="mb-2">Method 2: Destructured Props</Badge>
        <p><strong>Name:</strong> {name}</p>
        <p><strong>Age:</strong> {age}</p>
        <p className="mb-0"><strong>City:</strong> {city}</p>
      </Card.Body>
    </Card>
  );
};

// METHOD 3: Default Props
const UserCard3 = ({ name = 'Guest', age = 18, role = 'User' }) => {
  console.log('👤 Method 3 - Props with Defaults:', { name, age, role });
  return (
    <Card className="mb-3">
      <Card.Body>
        <Badge bg="warning" className="mb-2">Method 3: Default Props</Badge>
        <p><strong>Name:</strong> {name}</p>
        <p><strong>Age:</strong> {age}</p>
        <p className="mb-0"><strong>Role:</strong> {role}</p>
      </Card.Body>
    </Card>
  );
};

// METHOD 4: Children Props (special prop for nested content)
const CardWrapper = ({ title, children, variant = 'primary' }) => {
  console.log('📦 Method 4 - Children Props:', { title, hasChildren: !!children, variant });
  return (
    <Card className="mb-3" border={variant}>
      <Card.Header className={`bg-${variant} text-white`}>{title}</Card.Header>
      <Card.Body>
        <Badge bg="info" className="mb-2">Method 4: Children Props</Badge>
        <div>{children}</div>
      </Card.Body>
    </Card>
  );
};

// METHOD 5: Spread Props (passing all props at once)
const UserCard5 = (props) => {
  const { name, ...otherProps } = props;
  console.log('📤 Method 5 - Spread Props:', { name, otherProps });
  console.log('   All props:', props);
  
  return (
    <Card className="mb-3">
      <Card.Body>
        <Badge bg="danger" className="mb-2">Method 5: Spread Props</Badge>
        <p><strong>Name:</strong> {name}</p>
        <div>
          <strong>Other Props:</strong>
          <ul className="mb-0">
            {Object.entries(otherProps).map(([key, value]) => (
              <li key={key}>{key}: {String(value)}</li>
            ))}
          </ul>
        </div>
      </Card.Body>
    </Card>
  );
};

// Demonstrating Props Flow (Parent to Child)
const ParentComponent = () => {
  const userData = {
    name: 'Priya',
    age: 28,
    city: 'Mumbai',
    country: 'India',
    occupation: 'Developer'
  };

  console.log('🔽 Parent Component: Passing props to children', userData);

  return (
    <Card className="mb-3">
      <Card.Header className="bg-dark text-white">Parent-to-Child Props Flow</Card.Header>
      <Card.Body>
        <Alert variant="info">
          <strong>Parent Component Data:</strong> {JSON.stringify(userData)}
        </Alert>
        <ChildComponent data={userData} />
      </Card.Body>
    </Card>
  );
};

const ChildComponent = ({ data }) => {
  console.log('   🔼 Child Component: Received props from parent', data);
  
  return (
    <div className="demo-box">
      <h6>Child Component Displaying Parent's Data:</h6>
      <p className="mb-0">{data.name} ({data.age}) from {data.city}, {data.country} - {data.occupation}</p>
    </div>
  );
};

// Main Lesson Component
const Lesson02_Props = () => {
  useEffect(() => {
    console.log('📖 Lesson 02: Props - Loaded');
    console.log('📚 Learning Objectives:');
    console.log('   1. Understand props basics');
    console.log('   2. Learn destructuring patterns');
    console.log('   3. Master default props');
    console.log('   4. Use children props');
    console.log('   5. Apply spread operator with props');
  }, []);

  const userProps = { 
    name: 'Alice', 
    age: 25, 
    city: 'Delhi',
    country: 'India',
    hobby: 'Reading'
  };

  return (
    <LessonLayout 
      lessonNumber={2}
      title="Props - Multiple Ways to Use" 
      description="Mastering different patterns for passing and using props in React"
    >
      <Alert variant="success">
        <strong>What You'll Learn:</strong>
        <ul className="mb-0 mt-2">
          <li>5 different ways to use props</li>
          <li>Props destructuring patterns</li>
          <li>Default props values</li>
          <li>Children props for composition</li>
          <li>Spread operator with props</li>
        </ul>
      </Alert>

      <h4 className="mt-4">Props Methods Demonstration</h4>
      
      <Row>
        <Col md={6}>
          <UserCard1 name="John Doe" age={30} />
        </Col>
        <Col md={6}>
          <UserCard2 name="Jane Smith" age={28} city="Bangalore" />
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <UserCard3 />
          <small className="text-muted">↑ No props passed - using defaults</small>
        </Col>
        <Col md={6}>
          <UserCard3 name="Raj Kumar" age={35} role="Admin" />
          <small className="text-muted">↑ Custom props override defaults</small>
        </Col>
      </Row>

      <h4 className="mt-4">Children Props Example</h4>
      <CardWrapper title="User Information" variant="success">
        <p>This content is passed as <code>children</code> prop!</p>
        <p className="mb-0">The CardWrapper component wraps this content.</p>
      </CardWrapper>

      <h4 className="mt-4">Spread Props Example</h4>
      <UserCard5 {...userProps} />

      <h4 className="mt-4">Parent-to-Child Props Flow</h4>
      <ParentComponent />

      <h4 className="mt-4">📊 Props Patterns Comparison</h4>
      <Card>
        <Card.Body>
          <table className="table comparison-table">
            <thead>
              <tr>
                <th>Method</th>
                <th>Syntax</th>
                <th>When to Use</th>
                <th>Pros</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Basic Props</td>
                <td><code>(props) =&gt; props.name</code></td>
                <td>When using many props</td>
                <td>Access all props easily</td>
              </tr>
              <tr>
                <td>Destructured</td>
                <td><code>{`({ name, age }) => ...`}</code></td>
                <td>Most common, clean code</td>
                <td>Clear what props are used</td>
              </tr>
              <tr>
                <td>Default Props</td>
                <td><code>{`({ name = 'Guest' }) => ...`}</code></td>
                <td>Optional props with fallbacks</td>
                <td>Prevents undefined errors</td>
              </tr>
              <tr>
                <td>Children</td>
                <td><code>{`({ children }) => ...`}</code></td>
                <td>Wrapper components</td>
                <td>Flexible composition</td>
              </tr>
              <tr>
                <td>Spread</td>
                <td><code>{`{...props}`}</code></td>
                <td>Forwarding many props</td>
                <td>Pass all props at once</td>
              </tr>
            </tbody>
          </table>
        </Card.Body>
      </Card>

      <Alert variant="warning" className="mt-4">
        <strong>💡 Best Practices:</strong>
        <ul className="mb-0">
          <li>Use destructuring for clarity and cleaner code</li>
          <li>Provide default values for optional props</li>
          <li>Use children prop for flexible component composition</li>
          <li>Props are read-only - never modify them directly</li>
        </ul>
      </Alert>
    </LessonLayout>
  );
};

export default Lesson02_Props;
